import React, {useContext, useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import moment from "moment";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {OrderUserType} from "../../../constants/order";
import {
  calculateTransactionFee,
  formatPrice,
  getSellerPayoutBreakdown,
} from "../../utils/utils";
import {orderStatuses, paymentStatuses, shippingStatuses} from "../../../constants/shipping";
import {OrderItemActions} from "../../../routes/Orders/Sales/OrderItemActions";
import {HistoryDialog} from "../HistoryDialog/HistoryDialog";
import {OrderPaymentMethodDisplay} from "../OrderPaymentMethodDisplay/OrderPaymentMethodDisplay";
import {ToasterContext} from "../Toaster/ToasterContext";
import {NotificationType} from "../../../constants/global";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { OrderTotalsSummary } from "../OrderTotalsSummary/OrderTotalsSummary";

interface OrdersGroupItemProps {
  orders: any;
  refetch: any;
  refetchLoading?: boolean;
}

export const OrdersGroupItem:React.FunctionComponent<OrdersGroupItemProps> = ({
  orders,
  refetch,
  refetchLoading = false,
}) => {
  const {addToast} = useContext(ToasterContext);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedOrders, setSelectedOrders] = useState<any>(null);

  const getTotalAmount = (sellerOrders: any) => {
    const filteredOrders = sellerOrders.filter((order: any) => order.status === 1);

    let total = 0;
    filteredOrders.forEach((item: any) => {
      total = total + Number(item.totalAmount);
    });

    return formatPrice(total)
  }

  const getFedexInsurance = (sellerOrders: any) => {
    const filteredOrders = sellerOrders.filter((order: any) => order.status === 1);

    return filteredOrders[0]?.fedExInsurance || '0';

  }

  const handleScrollHorizontal = () => {
    const currentScrollPos = document.querySelector(
        ".stickyPaper"
    )?.scrollLeft || 0;
    const firstColumnHeader = document.querySelector<HTMLElement>(".stickyCell")
    if (firstColumnHeader) {
      firstColumnHeader.style.transform = `translate3d(${currentScrollPos}px, 0px, 0px)`;
    }
  }

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    let virtualScrollerElement: HTMLElement | null;
    const findVirtualScroller = () => {
        virtualScrollerElement = document.querySelector(
            ".stickyPaper"
        );
        if (!virtualScrollerElement) {
            timer = setTimeout(findVirtualScroller, 100);
        } else {
            virtualScrollerElement.addEventListener(
                "scroll",
                handleScrollHorizontal
            );
        }
    };
    findVirtualScroller();
    return () => {
      timer && clearTimeout(timer);
      virtualScrollerElement && virtualScrollerElement.removeEventListener(
        "scroll",
        handleScrollHorizontal
      );
    };
  }, []);

  const getTotalShippingFee = (sellerOrders: any) => {
    const filteredOrders = sellerOrders.filter((order: any) => order.status === 1);

    let total = 0;
    filteredOrders.forEach((item: any) => {
      total = total + Number(item.shippingFee);
    });

    return `${total.toFixed(2)}`
  }

  const getTradeNetFee = (order: any) => {
    const { totalAmount, tradeNetFee } = order;

    return Number(totalAmount) * Number(tradeNetFee) / 100
  }
  
  // Helper to strip "o_" prefix from orderNumber keys for display
  const stripOrderPrefix = (orderKey: string): string => {
    return orderKey.startsWith('o_') ? orderKey.slice(2) : orderKey;
  };

  const isTheSameName = (items: any) => {
    let isTheSame = false;
    let name: string = items[0].split('_')[0];

    items.forEach((item: string) => {
      const nameItems = item.split('_');

      if (name !== nameItems[0]) {
        name = nameItems[0];
        isTheSame = false;
      } else {
        isTheSame = true;
      }
    })

    return isTheSame;
  }

  return (
    <Box>
      <OrderTotalsSummary orders={orders} />
      <TableContainer component={Paper} sx={{ maxHeight: 440 }} className="stickyPaper">
        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left" className="stickyCell">Buyer</TableCell>            
              <TableCell align="left">Order Number</TableCell>
              <TableCell align="left">Order Date</TableCell>
              <TableCell align="left">Shipping Method</TableCell>
              <TableCell align="left">Payment Method</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="left">Tracking ID</TableCell>
              <TableCell align="center">Buyer payment</TableCell>
              <TableCell align="center">Buyer shipping Fee</TableCell>
              <TableCell align="center">Stripe transaction fee</TableCell>
              <TableCell align="center">Transaction fee</TableCell>
              <TableCell align="center">Seller tradeNetRx fee</TableCell>
              <TableCell align="center">Seller fedex insurance</TableCell>
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(orders).map((orderKey: any, key: number) => {
              const orderNumber = stripOrderPrefix(orderKey); // Strip "o_" prefix for display
              return (
                Object.keys(orders[orderKey]).sort().map((seller: string, key: number) => (
                <TableRow
                  key={seller}
                  sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                  onClick={() => {
                    setSelectedOrders(orders[orderKey][seller]); // Use orderKey (with prefix) for lookup
                    setOpen(true)
                  }}
                >
                  <TableCell component="th" scope="row" className="stickyCol">
                    {(key === 0 || !isTheSameName(Object.keys(orders[orderKey]))) && (
                      orders[orderKey][seller][0].Buyer.legalName
                    )}
                  </TableCell>
                  <TableCell align="left">
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      {orderNumber}
                      <ContentCopyIcon
                        onClick={(event: any) => {
                          event.stopPropagation();
                          navigator.clipboard.writeText(orderNumber);
                          addToast({ type: NotificationType.Success, title: 'Copied to Clipboard' });
                        }}
                        fontSize='small'
                        sx={{ml: 1, cursor: 'pointer'}}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="left">{moment(orders[orderKey][seller][0]?.createdAt).format("MM/DD/YYYY")}</TableCell>
                  <TableCell align="left">{orders[orderKey][seller][0]?.ShippingMethod.name}</TableCell>
                  <TableCell align="left">
                    <OrderPaymentMethodDisplay order={orders[orderKey][seller][0]} />
                  </TableCell>
                  <TableCell align="left">
                    {orders[orderKey][seller][0]?.status === 0 || orders[orderKey][seller][0]?.status === 2 ? (
                      <Box sx={{ maxWidth: '80px', fontSize: '12px', borderRadius: '16px', color: orderStatuses[orders[orderKey][seller][0]?.status].color, backgroundColor: orderStatuses[orders[orderKey][seller][0]?.status].bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2, pr: 2, pt: 0.5, pb: 0.5}}>
                        {orderStatuses[orders[orderKey][seller][0]?.status].label}
                      </Box>
                    ) : (
                      <>
                        {((orders[orderKey][seller][0]?.shippingStatus === 5 && orders[orderKey][seller][0]?.paymentStatus === 1) || orders[orderKey][seller][0]?.paymentStatus === 2) ? (
                          <Box sx={{ maxWidth: '80px', textAlign: 'center', fontSize: '12px', borderRadius: '16px', color: paymentStatuses[orders[orderKey][seller][0]?.paymentStatus].color, backgroundColor: paymentStatuses[orders[orderKey][seller][0]?.paymentStatus].bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2, pr: 2, pt: 0.5, pb: 0.5}}>
                            {paymentStatuses[orders[orderKey][seller][0]?.paymentStatus].label}
                          </Box>
                        ) : (
                          <Box sx={{ maxWidth: '80px', textAlign: 'center', fontSize: '12px', borderRadius: '16px', color: shippingStatuses[orders[orderKey][seller][0]?.shippingStatus].color, backgroundColor: shippingStatuses[orders[orderKey][seller][0]?.shippingStatus].bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2, pr: 2, pt: 0.5, pb: 0.5}}>
                            {shippingStatuses[orders[orderKey][seller][0]?.shippingStatus].label}
                          </Box>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {orders[orderKey][seller][0]?.status === 0 || orders[orderKey][seller][0]?.status === 2 ? '-' : (
                      <Box sx={{display: 'flex', alignItems: 'center'}}>
                        {orders[orderKey][seller][0]?.trackingNumber || '-'}
                        {orders[orderKey][seller][0]?.trackingNumber && (
                          <ContentCopyIcon
                            onClick={(event: any) => {
                              event.stopPropagation();
                              navigator.clipboard.writeText(orders[orderKey][seller][0]?.trackingNumber);
                              addToast({ type: NotificationType.Success, title: 'Copied to Clipboard' });
                            }}
                            fontSize='small'
                            sx={{ml: 1, cursor: 'pointer'}}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {`$${formatPrice(getTotalAmount(orders[orderKey][seller]))}`}
                  </TableCell>
                  <TableCell align="center">
                    {`$${formatPrice(getTotalShippingFee(orders[orderKey][seller]))}`}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const txFee =
                        getSellerPayoutBreakdown(
                          orders[orderKey][seller].filter((o: any) => o.status === 1)
                        )?.transactionFee ??
                        calculateTransactionFee(Number(getTotalAmount(orders[orderKey][seller])));
                      return txFee > 0 ? `$${formatPrice(txFee)}` : '—';
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const cardFee =
                        getSellerPayoutBreakdown(
                          orders[orderKey][seller].filter((o: any) => o.status === 1)
                        )?.stripeCardFee ?? 0;
                      return cardFee > 0 ? `$${formatPrice(cardFee)}` : '—';
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {`$${formatPrice(
                      getSellerPayoutBreakdown(
                        orders[orderKey][seller].filter((o: any) => o.status === 1)
                      )?.tradeNetFee ??
                        Number(getTotalAmount(orders[orderKey][seller])) *
                          Number(orders[orderKey][seller][0].tradeNetFee) /
                          100
                    )}`}
                  </TableCell>
                  <TableCell align="center">
                    {`$${formatPrice(getFedexInsurance(orders[orderKey][seller]))}`}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    {orders[orderKey][seller][0]?.status === 1 && (
                      <OrderItemActions
                        key={orderNumber}
                        item={orders[orderKey][seller][0]}
                        orders={orders[orderKey][seller]}
                        user={OrderUserType.seller}
                        refetch={refetch}
                        refetchLoading={refetchLoading}
                        isAdmin={true}
                        isColumn={true}
                      />
                    )}
                  </TableCell>
                </TableRow>
                ))
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {open && (
        <HistoryDialog
          orders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          open={open}
          setOpen={setOpen}
          user={OrderUserType.seller}
        />
      )}
    </Box>
  )
}

export default OrdersGroupItem;